import { useAuth } from '../../context/AuthContext';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel,
  ToggleButtonGroup, ToggleButton, TextField, InputAdornment, Button,
} from '@mui/material';

const CARD  = { backgroundColor: '#131318', border: '1px solid rgba(245,166,35,0.25)', borderRadius: 2, overflow: 'hidden' };
const TH    = { backgroundColor: '#0e0e12', color: '#ffffff', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase', py: 0.9, px: 1, borderBottom: '1px solid rgba(245,166,35,0.20)', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2 };
const SCROLL = { scrollbarWidth: 'thin', scrollbarColor: 'rgba(245,166,35,0.3) transparent', '&::-webkit-scrollbar': { width: 4, height: 4 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(245,166,35,0.3)', borderRadius: 8 } };

const TEAM_LOGO = abbr => `https://a.espncdn.com/i/teamlogos/nfl/500/${abbr?.toLowerCase()}.png`;
const POS_FILTERS = ['ALL', 'QB', 'RB', 'WR', 'TE', 'OT', 'OG', 'C', 'DE', 'DT', 'LB', 'CB', 'S'];

// ── Draft Order (left panel) ─────────────────────────────────────────────────
let _orderCache = null;
function DraftOrder() {
  const [data, setData]     = useState(null);
  const [round, setRound]   = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (_orderCache) { setData(_orderCache); setLoading(false); return; }
    fetch('/data/draft_order_2026.json').then(r => r.json()).then(d => {
      _orderCache = d; setData(d); setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} sx={{ color: '#f5a623' }} /></Box>;
  if (!data) return null;

  const rounds = [...new Set(data.picks.map(p => p.round))].sort((a,b)=>a-b);
  const picks = data.picks.filter(p => p.round === round).sort((a,b) => a.overall - b.overall);

  return (
    <Box sx={{ ...CARD, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(245,166,35,0.15)', backgroundColor: '#0e0e12' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', color: '#f5a623', textTransform: 'uppercase' }}>
          📋 2026 Draft Order
        </Typography>
        <Typography sx={{ fontSize: '0.62rem', color: '#64748b', mt: 0.25 }}>
          April 23–25 · Pittsburgh{round > 1 ? ' · R2–7 order approximate, trades pending' : ' · R1 exact'}
        </Typography>
      </Box>

      {/* Round tabs — ToggleButtonGroup style */}
      <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid rgba(245,166,35,0.1)', backgroundColor: '#0a0a0a', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        <ToggleButtonGroup value={round} exclusive onChange={(_, v) => v && setRound(v)} size="small">
          {rounds.map(r => (
            <ToggleButton key={r} value={r} sx={{
              color: round===r ? '#f5a623' : '#64748b',
              borderColor: 'rgba(245,166,35,0.2)',
              backgroundColor: round===r ? 'rgba(245,166,35,0.08)' : 'transparent',
              fontWeight: 800, fontSize: '0.62rem', px: 1.1, py: 0.35,
              '&:hover': { backgroundColor: 'rgba(245,166,35,0.05)' },
            }}>
              Rd {r}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1, ...SCROLL }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ ...TH, width: 36 }}>#</TableCell>
              <TableCell align="center" sx={{ ...TH, width: 56 }}>Team</TableCell>
              <TableCell sx={TH}>Player</TableCell>
              <TableCell align="center" sx={{ ...TH, width: 44 }}>Pos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {picks.map(p => (
              <TableRow key={p.overall} sx={{ '& td': { borderBottom: '1px solid rgba(245,166,35,0.06)' }, '&:hover td': { backgroundColor: 'rgba(245,166,35,0.04)' } }}>
                <TableCell align="center" sx={{ py: 0.4, color: '#475569', fontWeight: 700, fontSize: '0.7rem', backgroundColor: 'transparent' }}>
                  {p.overall}
                </TableCell>
                <TableCell sx={{ py: 0.4, backgroundColor: 'transparent' }}>
                  <Tooltip title={p.teamName || p.teamAbbr} arrow placement="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <img src={TEAM_LOGO(p.teamAbbr)} alt={p.teamAbbr} width={20} height={20} style={{ objectFit: 'contain', borderRadius: 2 }} onError={e => { e.target.style.display='none'; }} />
                      {p.traded && <Chip label="T" size="small" sx={{ height: 13, fontSize: '0.48rem', fontWeight: 900, bgcolor: 'rgba(6,182,212,0.15)', color: '#06b6d4', border: '1px solid rgba(6,182,212,0.3)', px: 0.2 }} />}
                  {p.comp && <Chip label="COMP" size="small" sx={{ height: 13, fontSize: '0.44rem', fontWeight: 900, bgcolor: 'rgba(245,166,35,0.1)', color: '#f5a623', border: '1px solid rgba(245,166,35,0.3)', px: 0.3 }} />}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ py: 0.4, backgroundColor: 'transparent' }}>
                  {p.player
                    ? <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#ffffff' }}>{p.player}</Typography>
                    : <Typography sx={{ fontSize: '0.72rem', color: '#334155', fontStyle: 'italic' }}>TBD</Typography>
                  }
                </TableCell>
                <TableCell align="center" sx={{ py: 0.4, backgroundColor: 'transparent' }}>
                  {p.pos
                    ? <Chip label={p.pos} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 800, bgcolor: 'rgba(245,166,35,0.1)', color: '#f5a623', border: '1px solid rgba(245,166,35,0.25)' }} />
                    : null
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

// ── Draft Consensus Board (right panel) ──────────────────────────────────────
let _boardCache = null;
let _faCache = null;

const SOURCES = ['dj', 'kiper', 'sn', 'cbs'];
const SOURCE_SHORT = { dj: 'NFL.com', kiper: 'ESPN', sn: 'Spt.News', cbs: 'CBS' };
const SOURCE_TIP   = { dj: 'Daniel Jeremiah — NFL.com (v3.0, Mar 5)', kiper: 'Mel Kiper Jr. — ESPN (Mar 5)', sn: 'Vinnie Iyer — Sporting News (Mar 1)', cbs: 'Josh Renner — CBS Sports (Nov 2025, pre-combine)' };

function DraftConsensus() {
  const FREE_DRAFT_ROWS = 15;
  const { isPremium, isBasic, setAuthModalOpen, setUpgradeModalOpen } = useAuth();
  const [data, setData]       = useState(null);
  const [faPlayers, setFa]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [pos, setPos]         = useState('ALL');
  const [orderBy, setOrderBy] = useState('consensus');
  const [order, setOrder]     = useState('asc');
  const [search, setSearch]   = useState('');

  useEffect(() => {
    const loads = [];
    if (_boardCache) { setData(_boardCache); } else {
      loads.push(fetch('/data/draft_consensus_2026.json').then(r=>r.json()).then(d=>{ _boardCache=d; setData(d); }));
    }
    if (_faCache) { setFa(_faCache); } else {
      loads.push(fetch('/data/free_agents_2026.json').then(r=>r.json()).then(d=>{ _faCache=d.players; setFa(d.players); }));
    }
    Promise.all(loads).finally(()=>setLoading(false));
  }, []);

  if (loading || !data) return <Box sx={{ py: 4, display:'flex', justifyContent:'center' }}><CircularProgress size={22} sx={{color:'#f5a623'}}/></Box>;

  const norm = n => n.toLowerCase().replace(/[.'`']/g,'').replace(/\s+/g,' ').trim();
  const nrRankMap = {};
  if (faPlayers.length) {
    const byPos = {};
    faPlayers.forEach(p => { const pg = p.posGroup||p.pos; if (!byPos[pg]) byPos[pg]=[]; if(p.nerdRating) byPos[pg].push(p); });
    Object.values(byPos).forEach(pool => {
      pool.sort((a,b)=>b.nerdRating-a.nerdRating).forEach((p,i)=>{ nrRankMap[norm(p.name)] = i+1; });
    });
  }

  const players = data.players.map(p => {
    const ranks = SOURCES.map(s => p[s]).filter(r => r != null);
    const consensus = ranks.length ? +(ranks.reduce((s,r)=>s+r,0)/ranks.length).toFixed(1) : null;
    const nrRank = nrRankMap[norm(p.name)] || null;
    return { ...p, consensus, rankCount: ranks.length, nrRank };
  });

  const posFilter = p => pos === 'ALL' ? true : p.pos === pos;

  const visiblePlayers = isPremium ? players : players.slice(0, FREE_DRAFT_ROWS);
  const lockedCount = players.length - visiblePlayers.length;

  const sorted = [...visiblePlayers]
    .filter(posFilter)
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => {
      const av = a[orderBy] ?? (order==='asc' ? Infinity : -Infinity);
      const bv = b[orderBy] ?? (order==='asc' ? Infinity : -Infinity);
      return order === 'asc' ? av - bv : bv - av;
    });

  const handleSort = key => {
    if (orderBy===key) setOrder(o => o==='asc'?'desc':'asc');
    else { setOrderBy(key); setOrder('asc'); }
  };

  const SortCell = ({ colKey, label, tip, width }) => (
    <TableCell align="center" sx={{ ...TH, width: width || 'auto' }}>
      <Tooltip title={tip} placement="top" arrow>
        <TableSortLabel active={orderBy===colKey} direction={orderBy===colKey?order:'asc'} onClick={()=>handleSort(colKey)}
          sx={{ '& .MuiTableSortLabel-icon': { color: '#f5a623 !important' }, color: 'inherit' }}>
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );

  return (
    <Box sx={{ ...CARD, display:'flex', flexDirection:'column', height:'100%' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1, borderBottom:'1px solid rgba(245,166,35,0.15)', backgroundColor:'#0e0e12', display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
        <Box>
          <Typography sx={{ fontSize:'0.72rem', fontWeight:800, letterSpacing:'0.08em', color:'#f5a623', textTransform:'uppercase' }}>
            🎓 Consensus Big Board 2026
          </Typography>
          <Typography sx={{ fontSize:'0.62rem', color:'#64748b', mt:0.25 }}>
            DJ · Kiper · Sporting News · CBS — averaged
          </Typography>
        </Box>
        <ToggleButtonGroup value={pos} exclusive onChange={(_,v)=>v&&setPos(v)} size="small">
          {POS_FILTERS.map(g => (
            <ToggleButton key={g} value={g} sx={{ color:pos===g?'#f5a623':'#64748b', borderColor:'rgba(245,166,35,0.2)', backgroundColor:pos===g?'rgba(245,166,35,0.08)':'transparent', fontWeight:800, fontSize:'0.6rem', px:1, py:0.3, '&:hover':{backgroundColor:'rgba(245,166,35,0.05)'} }}>{g}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField size="small" placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}
          InputProps={{ startAdornment:<InputAdornment position="start"><span style={{color:'#475569',fontSize:'0.8rem'}}>🔍</span></InputAdornment> }}
          sx={{ width:140, ml:'auto', '& .MuiOutlinedInput-root':{backgroundColor:'#0a0a0a',fontSize:'0.8rem'}, '& .MuiOutlinedInput-root fieldset':{borderColor:'rgba(245,166,35,0.2)'}, '& input':{color:'#ffffff',py:0.6} }} />
      </Box>

      <Box sx={{ overflowX:'auto', flex:1, ...SCROLL }}>
        <Table size="small" stickyHeader sx={{ minWidth: 400 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ ...TH, width:32 }}>Rk</TableCell>
              <TableCell align="center" sx={{ ...TH, width:40 }}>Pos</TableCell>
              <TableCell sx={TH}>Prospect</TableCell>
              <TableCell sx={{ ...TH, width:55 }}>College</TableCell>
              {SOURCES.map(s => (
                <SortCell key={s} colKey={s} label={SOURCE_SHORT[s]} tip={SOURCE_TIP[s]} width={52} />
              ))}
              <SortCell colKey="nrRank" label="NR" tip="Our Nerd Rating rank within position group — college prospects show — until they have NFL data" width={44} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((p, i) => (
              <TableRow key={p.name} sx={{ '& td':{borderBottom:'1px solid rgba(245,166,35,0.06)'}, '&:hover td':{backgroundColor:'rgba(245,166,35,0.04)'} }}>
                <TableCell align="center" sx={{ py:0.5, color:'#475569', fontWeight:700, fontSize:'0.7rem', backgroundColor:'transparent' }}>{i+1}</TableCell>
                <TableCell align="center" sx={{ py:0.5, backgroundColor:'transparent' }}>
                  <Chip label={p.pos} size="small" sx={{ height:15, fontSize:'0.55rem', fontWeight:800, bgcolor:'rgba(245,166,35,0.1)', color:'#f5a623', border:'1px solid rgba(245,166,35,0.25)' }} />
                </TableCell>
                <TableCell sx={{ py:0.5, backgroundColor:'transparent' }}>
                  <Typography sx={{ fontSize:'0.72rem', fontWeight:600, color:'#ffffff', whiteSpace:'normal', lineHeight:1.2 }}>{p.name}</Typography>
                </TableCell>
                <TableCell sx={{ py:0.5, backgroundColor:'transparent', color:'#64748b', fontSize:'0.62rem', maxWidth:55, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {p.college}
                </TableCell>
                {SOURCES.map(s => (
                  <TableCell key={s} align="center" sx={{ py:0.5, px:0.75, backgroundColor:'transparent', color:'#ffffff', fontSize:'0.72rem', fontVariantNumeric:'tabular-nums', width:52, minWidth:52 }}>
                    {p[s] != null ? `#${p[s]}` : <span style={{color:'#334155'}}>—</span>}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ py:0.5, px:0.75, backgroundColor:'transparent', color: p.nrRank ? '#10b981' : '#334155', fontFamily:'monospace', fontWeight:700, fontSize:'0.75rem', width:44, minWidth:44 }}>
                  {p.nrRank ? `#${p.nrRank}` : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Locked rows teaser */}
      {lockedCount > 0 && (
        <Box sx={{ borderTop: '1px solid rgba(245,166,35,0.15)' }}>
          {Array.from({ length: Math.min(lockedCount, 4) }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.9, opacity: 0.12 - i * 0.02, filter: 'blur(2px)', borderBottom: '1px solid rgba(245,166,35,0.04)' }}>
              <Box sx={{ width: 24, height: 10, borderRadius: 1, bgcolor: '#10b981' }} />
              <Box sx={{ width: 38, height: 10, borderRadius: 1, bgcolor: '#64748b' }} />
              <Box sx={{ flex: 1, height: 10, borderRadius: 1, bgcolor: '#334155' }} />
              <Box sx={{ width: 28, height: 10, borderRadius: 1, bgcolor: '#1e293b' }} />
            </Box>
          ))}
          <Box sx={{ textAlign: 'center', py: 2, px: 2, background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, transparent 100%)' }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#9eb8d0', mb: 1 }}>
              <strong style={{ color: '#f5a623' }}>{lockedCount} more prospects</strong> on the full board
            </Typography>
            {isBasic ? (
              <Button size="small" variant="contained" color="primary" onClick={() => setAuthModalOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}>
                Sign Up Free →
              </Button>
            ) : (
              <Button size="small" variant="contained" onClick={() => setUpgradeModalOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f5a623', color: '#000', '&:hover': { bgcolor: '#d4890f' } }}>
                Unlock Nerds+ →
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ px:2, py:0.75, borderTop:'1px solid rgba(245,166,35,0.1)' }}>
        <Typography sx={{ fontSize:'0.62rem', color:'#334155' }}>
          DJ v3.0 · Kiper (Mar 5) · Sporting News (Mar 1) · CBS/Renner (Nov '25) · Updates as boards refresh
        </Typography>
      </Box>
    </Box>
  );
}

// ── Main DraftExplorer ────────────────────────────────────────────────────────
export default function DraftExplorer() {
  return (
    <Box sx={{ display:'flex', gap:2, alignItems:'stretch', minHeight:600 }}>
      {/* Left — Draft Order */}
      <Box sx={{ width: 320, flexShrink: 0 }}>
        <DraftOrder />
      </Box>
      {/* Right — Consensus Big Board */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <DraftConsensus />
      </Box>
    </Box>
  );
}
